(() => {
  const initialise = root => {
    if (!root || root.dataset.motionReady) return;
    const choices = [...root.querySelectorAll('[data-showcase-choice]')];
    const stage = root.querySelector('[data-showcase-link]');
    let image = root.querySelector('[data-showcase-image]');
    if (!choices.length || !image || !stage) return;
    root.dataset.motionReady = 'true';
    const lifetime = new AbortController();
    const listen = (target, name, callback, options = {}) => target.addEventListener(name, callback, { ...options, signal: lifetime.signal });
    const caption = root.querySelector('.pp-hero-showcase__caption');
    const title = root.querySelector('[data-showcase-title]');
    const action = root.querySelector('[data-showcase-cta]');
    const control = root.querySelector('[data-hero-motion]');
    const progress = root.querySelector('[data-showcase-progress]');
    const phrase = root.querySelector('[data-hero-typewriter]');
    const accessiblePhrase = root.querySelector('[data-hero-phrase]');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const key = 'pp-hero-session-v2';
    const handle = choice => new URL(choice.href).pathname.split('/').pop();
    const handles = choices.map(handle);
    let stored;
    try { stored = JSON.parse(sessionStorage.getItem(key)); } catch {}
    const valid = stored && Array.isArray(stored.order) && stored.order.length === handles.length && new Set(stored.order).size === handles.length && stored.order.every(name => handles.includes(name));
    const order = valid ? stored.order : [...handles];
    if (!valid) {
      for (let index = order.length - 1; index > 0; index--) {
        const random = crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296;
        const swap = Math.floor(random * (index + 1));
        [order[index], order[swap]] = [order[swap], order[index]];
      }
    }
    let position = valid && Number.isInteger(stored.position) && stored.position >= 0 && stored.position < order.length ? stored.position : 0;
    const campaign = new URLSearchParams(location.search).get('pp_category');
    if (handles.includes(campaign)) position = order.indexOf(campaign);
    let requestedPosition = position;
    let manualPause = valid ? stored.paused === true : false;
    let visible = true;
    let menuOpen = Boolean(document.body.dataset.ppMenu);
    let interacting = false;
    let interactionOverride = false;
    let busy = false;
    let elapsed = 0;
    let version = 0;
    let animations = [];
    let drift;
    let lastTick = performance.now();
    let frame;
    let suppressClick = false;
    let typing;
    let currentChoice = choices[0];
    const cache = new Map();
    const framing = {
      stickers: ['50% 58%', '48% 52%', 1, '120%', '#09080b9c'],
      keyrings: ['52% 55%', '54% 50%', -1, '120%', '#09080b8c'],
      beanies: ['48% 50%', '50% 62%', 1, '112%', '#09080b9c'],
      lanyards: ['50% 57%', '58% 48%', -1, '118%', '#09080b8c'],
      mugs: ['50% 66%', '55% 66%', 1, '118%', '#09080b94'],
      socks: ['48% 56%', '52% 54%', -1, '120%', '#09080b8c'],
        'embroidered-keyrings': ['50% 60%', '46% 55%', 1, '118%', '#09080b8c'],
      'screen-printing': ['60% 48%', '80% 50%', -1, '108%', '#09080ba8'],
      embroidery: ['58% 54%', '64% 52%', 1, '110%', '#09080b9c'],
      'dtg-printing': ['58% 53%', '74% 54%', -1, '108%', '#09080ba8'],
      'dtf-printing': ['54% 56%', '82% 50%', 1, '108%', '#09080ba8']
    };
    const choiceAt = index => choices.find(choice => handle(choice) === order[index]);
    const save = () => { try { sessionStorage.setItem(key, JSON.stringify({ order, position, paused: manualPause })); } catch {} };
    const stopped = () => manualPause || reducedMotion.matches || document.hidden || !visible || menuOpen || (interacting && !interactionOverride);
    const syncMotion = () => {
      root.dataset.motionPaused = String(stopped());
      root.dataset.manualPaused = String(manualPause);
      root.dataset.motionVisible = String(visible);
      const paused = stopped();
      control.setAttribute('aria-label', paused ? 'Resume animation' : 'Pause animation');
      control.title = control.getAttribute('aria-label');
      control.setAttribute('aria-pressed', String(paused));
      control.querySelector('[data-play-icon]').hidden = !paused;
      control.querySelector('[data-pause-icon]').hidden = paused;
      if (drift) stopped() ? drift.pause() : drift.play();
      animations.forEach(animation => {
        if (reducedMotion.matches) animation.finish();
        else if (document.hidden || !visible || menuOpen) animation.pause();
        else animation.play();
      });
    };
    const applyFrame = (element, choice) => {
      const settings = framing[handle(choice)];
      element.style.setProperty('--scene-position', '70% 50%');
      element.style.setProperty('--scene-mobile-position', '50% 100%');
      element.style.setProperty('--scene-width', '100%');
      element.dataset.scene = handle(choice);
    };
    const typePhrase = choice => {
      typing?.cancel();
      const text = choice.dataset.title.toUpperCase();
      phrase.textContent = text;
      if (accessiblePhrase) accessiblePhrase.textContent = text;
      phrase.style.setProperty('--phrase-length', text.length);
      phrase.dataset.typing = String(!reducedMotion.matches);
      if (!reducedMotion.matches) {
        typing = phrase.animate([{ width: '0ch' }, { width: `${text.length}ch` }], { duration: Math.min(700, Math.max(450, text.length * 30)), easing: `steps(${text.length}, end)` });
        const currentTyping = typing;
        typing.finished.then(() => { if (typing === currentTyping) phrase.dataset.typing = 'false'; }).catch(() => {});
      }
    };
    const load = (choice, priority = 'low') => {
      const source = matchMedia('(max-width: 760px)').matches ? choice.dataset.mobileImage : choice.dataset.image;
      if (!cache.has(source)) {
        const incoming = new Image();
        incoming.decoding = 'async';
        incoming.fetchPriority = priority;
        incoming.src = source;
        cache.set(source, incoming.decode().then(() => incoming).catch(error => { cache.delete(source); throw error; }));
      }
      return cache.get(source);
    };
    const update = choice => {
      currentChoice = choice;
      stage.href = choice.href;
      stage.removeAttribute('aria-disabled');
      title.textContent = choice.dataset.title;
      action.textContent = `${choice.dataset.cta || 'Shop collection'} \u2192`;
      root.dataset.scene = handle(choice);
      root.querySelector('[data-showcase-count]').textContent = `${String(position + 1).padStart(2, '0')} / ${order.length}`;
      stage.style.setProperty('--scene-shade', framing[handle(choice)][4]);
      typePhrase(choice);
      progress.style.transform = 'scaleX(0)';
      progress.parentElement.style.setProperty('--scene-index', position);
      progress.parentElement.style.setProperty('--scene-count', order.length);
      elapsed = 0;
      save();
    };
    const startDrift = choice => {
      drift?.cancel();
      drift = null;
      if (reducedMotion.matches) return;
      const direction = framing[handle(choice)][2];
      drift = image.animate([{ transform: 'scale(1) translateX(0)' }, { transform: `scale(1.015) translateX(${direction * .25}%)` }], { duration: 5000, direction: 'alternate', iterations: Infinity, easing: 'linear' });
      syncMotion();
    };
    const cancelTransition = () => {
      animations.forEach(animation => animation.cancel());
      animations = [];
      stage.querySelectorAll('[data-showcase-outgoing]').forEach(element => element.remove());
      caption.querySelectorAll('[data-showcase-old-title]').forEach(element => element.remove());
    };
    const select = async (target, manual = false, initial = false) => {
      requestedPosition = (target + order.length) % order.length;
      const selectedPosition = requestedPosition;
      const choice = choiceAt(selectedPosition);
      const request = ++version;
      cancelTransition();
      stage.removeAttribute('href');
      stage.setAttribute('aria-disabled', 'true');
      stage.setAttribute('aria-busy', 'true');
      if (manual) { manualPause = true; save(); }
      busy = true;
      root.dataset.sceneLoading = 'true';
      syncMotion();
      let decoded;
      try { decoded = await load(choice, initial ? 'high' : 'auto'); } catch {
        if (request === version) {
          busy = false;
          root.dataset.sceneLoading = 'false';
          manualPause = true;
          requestedPosition = position;
          stage.href = currentChoice.href;
          stage.removeAttribute('aria-disabled');
          stage.removeAttribute('aria-busy');
          root.querySelector('[data-showcase-status]').textContent = 'Image unavailable. Please try another scene.';
          save();
          syncMotion();
        }
        return;
      }
      if (request !== version || !root.isConnected) return;
      cancelTransition();
      drift?.cancel();
      const direction = framing[handle(choice)][2];
      const half = reducedMotion.matches ? 0 : 225;
      const timing = { duration: half, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'both' };
      if (!initial && half) {
        animations = [
          image.animate([{ opacity: 1, transform: 'translateX(0)' }, { opacity: .3, transform: `translateX(${-direction}%)` }], timing),
          title.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(-105%)' }], timing)
        ];
        syncMotion();
        await Promise.all(animations.map(animation => animation.finished.catch(() => {})));
        if (request !== version) return;
      }
      cancelTransition();
      const outgoing = image;
      const incoming = decoded.cloneNode();
      incoming.alt = choice.dataset.title;
      incoming.dataset.showcaseImage = '';
      incoming.width = decoded.naturalWidth;
      incoming.height = decoded.naturalHeight;
      applyFrame(incoming, choice);
      (outgoing.closest('picture') || outgoing).replaceWith(incoming);
      image = incoming;
      position = selectedPosition;
      update(choice);
      root.querySelector('[data-showcase-status]').textContent = manual ? choice.dataset.title : '';
      const closed = direction > 0 ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)';
      animations = reducedMotion.matches ? [] : [
        incoming.animate(initial ? [{ transform: 'scale(1.025)' }, { transform: 'scale(1)' }] : [{ clipPath: closed, transform: `translateX(${direction}%)` }, { clipPath: 'inset(0)', transform: 'translateX(0)' }], { ...timing, duration: initial ? 450 : half }),
        title.animate([{ transform: 'translateY(105%)' }, { transform: 'translateY(0)' }], timing)
      ];
      syncMotion();
      await Promise.all(animations.map(animation => animation.finished.catch(() => {})));
      if (request !== version) return;
      cancelTransition();
      busy = false;
      root.dataset.sceneLoading = 'false';
      stage.removeAttribute('aria-busy');
      lastTick = performance.now();
      startDrift(choice);
      load(choiceAt((position + 1) % order.length)).catch(() => {});
    };
    listen(root.querySelector('[data-showcase-next]'), 'click', () => select(requestedPosition + 1, true));
    listen(root.querySelector('[data-showcase-prev]'), 'click', () => select(requestedPosition - 1, true));
    listen(control, 'click', () => { manualPause = !stopped(); interactionOverride = !manualPause; elapsed = 0; save(); syncMotion(); });
    listen(document, 'pp:menu-change', event => { menuOpen = Boolean(event.detail); syncMotion(); });
    listen(document, 'visibilitychange', syncMotion);
    listen(matchMedia('(max-width: 760px)'), 'change', () => select(position, false, true));
    const composition = root.querySelector('.pp-hero__composition');
    const syncInteraction = () => {
      interacting = composition.matches(':focus-within') || (matchMedia('(hover: hover)').matches && composition.matches(':hover'));
      if (!interacting) interactionOverride = false;
      syncMotion();
    };
    ['pointerenter', 'pointerleave', 'focusin', 'focusout'].forEach(name => listen(composition, name, () => requestAnimationFrame(syncInteraction)));
    listen(reducedMotion, 'change', () => { if (reducedMotion.matches) { drift?.cancel(); typing?.finish(); } else if (!busy) startDrift(choiceAt(position)); syncMotion(); });
    let touchStart;
    listen(stage, 'touchstart', event => { const touch = event.touches[0]; touchStart = { x: touch.clientX, y: touch.clientY }; suppressClick = false; }, { passive: true });
    listen(stage, 'touchend', event => {
      if (!touchStart) return;
      const touch = event.changedTouches[0];
      const distance = touch.clientX - touchStart.x;
      const vertical = touch.clientY - touchStart.y;
      touchStart = null;
      if (Math.abs(distance) > 55 && Math.abs(distance) > Math.abs(vertical) * 1.5) { suppressClick = true; select(requestedPosition + (distance < 0 ? 1 : -1), true); }
    }, { passive: true });
    listen(stage, 'touchcancel', () => { touchStart = null; });
    listen(stage, 'click', event => { if (suppressClick || stage.getAttribute('aria-disabled') === 'true') { event.preventDefault(); suppressClick = false; } });
    const explorer = root.querySelector('[data-explorer]');
    const tabs = [...explorer.querySelectorAll('[data-explorer-tab]')];
    const panels = [...explorer.querySelectorAll('[data-explorer-panel]')];
    let activePanel = panels[0];
    const previousCategories = explorer.querySelector('[data-explorer-prev]');
    const nextCategories = explorer.querySelector('[data-explorer-next]');
    const pauseCategories = explorer.querySelector('[data-explorer-pause]');
    const categoryLoops = new Map();
    let categoriesPaused = false;
    let categoriesVisible = false;
    let categoriesHeldUntil = 0;
    const wrapCategories = panel => {
      const loop = categoryLoops.get(panel);
      if (!loop || panel.classList.contains('is-dragging') || (panel.contains(document.activeElement) && document.activeElement !== panel)) return;
      const offset = panel.scrollLeft - loop.start;
      // scrollLeft is rounded by some browsers even when grid columns are fractional.
      if (offset < -1 || offset >= loop.distance + 1) panel.scrollLeft = loop.start + ((offset % loop.distance) + loop.distance) % loop.distance;
    };
    const buildCategories = () => {
      panels.filter(panel => !panel.hidden).forEach(panel => {
        const previous = categoryLoops.get(panel);
        const offset = previous ? panel.scrollLeft - previous.start : 0;
        const progress = previous && Math.abs(offset) > 1 ? (offset % previous.distance) / previous.distance : 0;
        panel.querySelectorAll('[data-explorer-copy]').forEach(copy => copy.remove());
        const originals = [...panel.children];
        const gap = parseFloat(getComputedStyle(panel).columnGap);
        const distance = originals.reduce((sum, item) => sum + item.getBoundingClientRect().width + gap, 0);
        if (!distance) return;
        const repeats = Math.ceil(panel.clientWidth / distance) + 1;
        const copies = () => {
          const fragment = document.createDocumentFragment();
          for (let repeat = 0; repeat < repeats; repeat++) originals.forEach(item => {
            const copy = item.cloneNode(true);
            copy.dataset.explorerCopy = '';
            copy.setAttribute('aria-hidden', 'true');
            copy.tabIndex = -1;
            copy.removeAttribute('data-showcase-choice');
            [copy, ...copy.querySelectorAll('[id],[data-shopify-editor-block]')].forEach(element => {
              element.removeAttribute('id');
              element.removeAttribute('data-shopify-editor-block');
            });
            fragment.append(copy);
          });
          return fragment;
        };
        panel.prepend(copies());
        panel.append(copies());
        const start = repeats * distance;
        categoryLoops.set(panel, { start, distance, remainder: 0 });
        panel.dataset.loopStart = start;
        panel.dataset.loopDistance = distance;
        panel.scrollLeft = start + ((progress + 1) % 1) * distance;
      });
      syncExplorer();
    };
    const syncExplorer = () => {
      previousCategories.disabled = nextCategories.disabled = false;
      pauseCategories.hidden = reducedMotion.matches;
      pauseCategories.setAttribute('aria-pressed', String(categoriesPaused));
      pauseCategories.setAttribute('aria-label', categoriesPaused ? 'Resume categories' : 'Pause categories');
      pauseCategories.title = pauseCategories.getAttribute('aria-label');
      pauseCategories.innerHTML = control.querySelector(categoriesPaused ? '[data-play-icon]' : '[data-pause-icon]').innerHTML;
    };
    const activate = tab => {
      tabs.forEach(item => { item.setAttribute('aria-selected', String(item === tab)); item.tabIndex = item === tab ? 0 : -1; });
      panels.forEach(panel => { panel.hidden = panel.dataset.explorerPanel !== tab.dataset.explorerTab; });
      activePanel = panels.find(panel => !panel.hidden);
      buildCategories();
      activePanel.scrollTo({ left: categoryLoops.get(activePanel)?.start || 0, behavior: 'instant' });
      activePanel.getAnimations().forEach(animation => animation.cancel());
      if (!reducedMotion.matches) activePanel.animate([{ opacity: .3, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 200, easing: 'ease-out' });
      syncExplorer();
    };
    tabs.forEach((tab, index) => {
      listen(tab, 'click', () => activate(tab));
      listen(tab, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next].focus(); activate(tabs[next]);
      });
    });
    panels.forEach((panel, index) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabs[index].id);
      panel.tabIndex = 0;
      if ('onscrollend' in panel) listen(panel, 'scrollend', () => wrapCategories(panel), { passive: true });
      else {
        let settle;
        listen(panel, 'scroll', () => { clearTimeout(settle); settle = setTimeout(() => wrapCategories(panel), 150); }, { passive: true });
        lifetime.signal.addEventListener('abort', () => clearTimeout(settle), { once: true });
      }
      ['wheel', 'touchstart', 'keydown'].forEach(name => listen(panel, name, () => { categoriesHeldUntil = performance.now() + 1800; }, { passive: true }));
      let drag;
      let dragged = false;
      listen(panel, 'dragstart', event => event.preventDefault());
      listen(panel, 'pointerdown', event => {
        categoriesHeldUntil = performance.now() + 1800;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragged = false;
        drag = { start: event.clientX, scroll: panel.scrollLeft, pointer: event.pointerId };
      });
      listen(panel, 'pointermove', event => {
        if (!drag) return;
        categoriesHeldUntil = performance.now() + 1800;
        const distance = event.clientX - drag.start;
        if (!dragged && Math.abs(distance) < 7) return;
        dragged = true;
        panel.classList.add('is-dragging');
        panel.setPointerCapture(drag.pointer);
        event.preventDefault();
        panel.scrollLeft = drag.scroll - distance;
      });
      const endDrag = () => {
        if (!drag) return;
        if (panel.hasPointerCapture(drag.pointer)) panel.releasePointerCapture(drag.pointer);
        drag = null;
        panel.classList.remove('is-dragging');
        if (dragged) {
          if (panel.contains(document.activeElement)) document.activeElement.blur();
          wrapCategories(panel);
        }
      };
      listen(panel, 'pointerup', endDrag);
      listen(panel, 'pointercancel', endDrag);
      listen(panel, 'lostpointercapture', endDrag);
      listen(panel, 'pointerleave', () => { if (!dragged) drag = null; });
      listen(panel, 'click', event => { if (dragged) { event.preventDefault(); event.stopPropagation(); dragged = false; } }, { capture: true });
    });
    [previousCategories, nextCategories].forEach((button, index) => listen(button, 'click', () => {
      categoriesHeldUntil = performance.now() + 1800;
      activePanel.scrollBy({ left: (activePanel.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(activePanel).columnGap)) * (index ? 1 : -1), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    }));
    listen(pauseCategories, 'click', () => { categoriesPaused = !categoriesPaused; syncExplorer(); });
    listen(reducedMotion, 'change', syncExplorer);
    explorer.querySelector('[data-explorer-controls]').hidden = false;
    explorer.querySelector('[data-explorer-tabs]').hidden = false;
    // Wait for the homepage directory merge before cloning category links.
    const startExplorer = () => activate(tabs[0]);
    if (document.readyState === 'loading') listen(document, 'DOMContentLoaded', startExplorer, { once: true });
    else requestAnimationFrame(startExplorer);
    listen(explorer, 'pp:explorer-ready', buildCategories);
    let explorerWidth = 0;
    const explorerObserver = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      // Image/font loading and mobile browser chrome can change height while scrolling.
      if (Math.abs(width - explorerWidth) < 1) return;
      explorerWidth = width;
      buildCategories();
    });
    explorerObserver.observe(explorer);
    const explorerVisibility = new IntersectionObserver(entries => { categoriesVisible = entries[0].isIntersecting; }, { threshold: .1 });
    explorerVisibility.observe(explorer);
    const trustTrack = root.querySelector('[data-trust-track]');
    const trustViewport = trustTrack?.parentElement;
    const trustControl = root.querySelector('[data-trust-pause]');
    let trustAnimation;
    let trustPaused = false;
    let trustVisible = false;
    let trustReduced = reducedMotion.matches;
    let trustWidth = 0;
    const syncTrust = () => {
      if (!trustAnimation) return;
      const paused = trustPaused || reducedMotion.matches || !trustVisible || document.hidden || trustViewport.matches(':hover, :focus-within');
      paused ? trustAnimation.pause() : trustAnimation.play();
      trustControl.setAttribute('aria-pressed', String(trustPaused));
      trustControl.setAttribute('aria-label', trustPaused ? 'Resume customer logos' : 'Pause customer logos');
      trustControl.title = trustControl.getAttribute('aria-label');
      trustControl.innerHTML = control.querySelector(trustPaused ? '[data-play-icon]' : '[data-pause-icon]').innerHTML;
    };
    const buildTrust = () => {
      if (!trustTrack) return;
      const width = trustViewport.clientWidth;
      if (trustAnimation && width === trustWidth && trustReduced === reducedMotion.matches) return;
      trustWidth = width;
      const previousDistance = Number(trustTrack.dataset.loopDistance);
      const offset = trustAnimation && previousDistance ? (Number(trustAnimation.currentTime) * 30 / 1000) % previousDistance : 0;
      trustAnimation?.cancel(); trustAnimation = null;
      trustTrack.querySelectorAll('[data-trust-copy]').forEach(copy => copy.remove());
      trustReduced = reducedMotion.matches;
      trustControl.hidden = trustReduced;
      trustViewport.classList.toggle('is-moving', !trustReduced);
      trustViewport.scrollLeft = 0;
      if (trustReduced) return;
      const originals = [...trustTrack.children];
      const gap = parseFloat(getComputedStyle(trustTrack).gap);
      const distance = originals.reduce((total, logo) => total + logo.getBoundingClientRect().width + gap, 0);
      const copies = Math.max(1, Math.ceil(trustViewport.clientWidth / distance));
      for (let repeat = 0; repeat < copies; repeat++) originals.forEach(logo => { const copy = logo.cloneNode(true); copy.dataset.trustCopy = ''; copy.setAttribute('aria-hidden', 'true'); copy.inert = true; copy.removeAttribute('data-shopify-editor-block'); trustTrack.append(copy); });
      trustTrack.dataset.loopDistance = distance;
      trustAnimation = trustTrack.animate([{ transform: 'translateX(0)' }, { transform: `translateX(-${distance}px)` }], { duration: distance / 30 * 1000, iterations: Infinity, easing: 'linear' });
      trustAnimation.currentTime = offset % distance / 30 * 1000;
      syncTrust();
    };
    const trustObserver = new IntersectionObserver(entries => {
      trustVisible = entries[0].isIntersecting;
      if (trustVisible) trustTrack.querySelectorAll('img').forEach(logo => { logo.loading = 'eager'; });
      syncTrust();
    }, { threshold: .1 });
    if (trustTrack) {
      trustObserver.observe(trustViewport);
      ['mouseenter', 'mouseleave', 'focusin', 'focusout'].forEach(name => listen(trustViewport, name, () => requestAnimationFrame(syncTrust)));
      listen(trustControl, 'click', () => { trustPaused = !trustPaused; syncTrust(); });
      listen(document, 'visibilitychange', syncTrust);
      listen(reducedMotion, 'change', buildTrust);
      listen(window, 'resize', buildTrust);
      buildTrust();
    }
    const visibilityObserver = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; syncMotion(); }, { threshold: .15 });
    visibilityObserver.observe(root.querySelector('.pp-hero__composition'));
    const header = document.querySelector('.site-header');
    const placeholder = header?.closest('.shopify-section');
    let headerDirty = true;
    const syncHeaderSize = () => {
      const overlap = placeholder ? `${placeholder.getBoundingClientRect().height}px` : '0px';
      if (root.style.getPropertyValue('--hero-overlap') !== overlap) root.style.setProperty('--hero-overlap', overlap);
      headerDirty = true;
    };
    listen(window, 'scroll', () => { headerDirty = true; }, { passive: true });
    listen(window, 'resize', syncHeaderSize);
    const headerSizeObserver = new ResizeObserver(syncHeaderSize);
    if (placeholder) headerSizeObserver.observe(placeholder);
    syncHeaderSize();
    const tick = now => {
      if (!root.isConnected) return;
      if (headerDirty) {
        headerDirty = false;
        const past = composition.getBoundingClientRect().bottom <= (header?.getBoundingClientRect().bottom || 100);
        if (document.body.classList.contains('pp-past-hero') !== past) document.body.classList.toggle('pp-past-hero', past);
      }
      if (categoriesVisible && !categoriesPaused && !reducedMotion.matches && !document.hidden && !menuOpen && now > categoriesHeldUntil && !activePanel.matches(':hover,:focus-within')) {
        const loop = categoryLoops.get(activePanel);
        if (loop) {
          // Accumulate subpixel movement so high refresh rate screens keep moving.
          loop.remainder += Math.min(now - lastTick, 100) * .035;
          const pixels = Math.floor(loop.remainder);
          activePanel.scrollLeft += pixels;
          loop.remainder -= pixels;
        }
        wrapCategories(activePanel);
      }
      if (trustReduced !== reducedMotion.matches) buildTrust();
      if (!stopped() && !busy) {
        elapsed += Math.min(now - lastTick, 100);
        progress.style.transform = `scaleX(${Math.min(elapsed / 5000, 1)})`;
        if (elapsed >= 5000) select(position + 1);
      }
      lastTick = now;
      frame = requestAnimationFrame(tick);
    };
    root.querySelector('[data-showcase-controls]').hidden = false;
    applyFrame(image, choices[0]);
    if (!reducedMotion.matches) root.querySelectorAll('.pp-hero__sub, .pp-hero__cta-wrap, .pp-hero__reassurance').forEach((element, index) => element.animate([{ opacity: .4, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 350, delay: 200 + index * 80, easing: 'ease-out' }));
    const revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      revealObserver.unobserve(entry.target);
      if (!reducedMotion.matches) entry.target.animate([{ opacity: .4, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 350, delay: Number(entry.target.dataset.revealDelay || 0), easing: 'ease-out' });
    }), { threshold: .15 });
    root.querySelectorAll('.pp-explorer__item, .pp-trust__heading').forEach((element, index) => { element.dataset.revealDelay = String(index % 4 * 45); revealObserver.observe(element); });
    if (!reducedMotion.matches) root.querySelectorAll('.pp-hero__line--1 > span').forEach((line, index) => line.animate([{ clipPath: 'inset(100% 0 0)', transform: 'translateY(12px)' }, { clipPath: 'inset(0)', transform: 'translateY(0)' }], { duration: 450, delay: index * 80, easing: 'ease-out' }));
    syncMotion();
    if (reducedMotion.matches && !valid && !handles.includes(campaign)) { position = order.indexOf(handle(choices[0])); requestedPosition = position; update(choices[0]); }
    else select(position, false, true);
    frame = requestAnimationFrame(tick);
    listen(window, 'pageshow', syncMotion);
    const dispose = () => {
      ++version;
      lifetime.abort();
      cancelAnimationFrame(frame);
      cancelTransition();
      drift?.cancel();
      typing?.cancel();
      visibilityObserver.disconnect();
      explorerObserver.disconnect();
      explorerVisibility.disconnect();
      headerSizeObserver.disconnect();
      trustAnimation?.cancel();
      trustObserver.disconnect();
      revealObserver.disconnect();
    };
    listen(document, 'shopify:section:unload', event => { if (event.target.contains(root)) dispose(); });
  };
  initialise(document.querySelector('.pp-hero--studio'));
  document.addEventListener('shopify:section:load', event => {
    initialise(event.target.querySelector('.pp-hero--studio'));
  });
})();
